import clsx, { ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { BREAKPOINTS } from '@/constant/breakpoints'
import { PAGES } from '@/constant/siteConstants'
import type {
  Breakpoint,
  PageSlug,
  PageType,
  SanityImage,
  SanityVideo,
} from '@/constant/types'

//* BROWSER
// ----------------
export const testMobileDevice = (includeTablet = false) => {
  const regexStr = `Android|webOS|iPhone|${
    includeTablet && 'iPad|'
  }iPod|BlackBerry|IEMobile|Opera Mini/i`
  const regex = new RegExp(regexStr)
  if (regex.test(navigator.userAgent)) {
    return true
  } else {
    return false
  }
}

export function getFromLocalStorage(key: string): string | null {
  if (typeof window !== 'undefined') {
    return window.localStorage.getItem(key)
  }
  return null
}

export function getFromSessionStorage(key: string): string | null {
  if (typeof sessionStorage !== 'undefined') {
    return sessionStorage.getItem(key)
  }
  return null
}

export const getSizes = (
  sizes: Partial<Record<Breakpoint, string>>,
  smallestSize: string,
): string => {
  const sizesEntries = Object.entries(sizes) as [Breakpoint, string][]
  const sizesStr = sizesEntries
    .map(([key, value]) => {
      return `${getMediaQueryFromScreen(BREAKPOINTS[key])} ${value}`
    })
    .join(', ')
  return `${sizesStr}, ${smallestSize}`
}

// * MISC
// ----------------
export const getSlugFromType = (type: PageType): PageSlug => {
  const page = Object.values(PAGES).find((page) => page.type === type)
  if (!page) return ''
  return page.slug
}

export const getTypeFromSlug = (
  pageSlug: PageSlug,
  secondarySlug?: string,
): PageType => {
  const page = Object.values(PAGES).find((page) => page.slug === pageSlug)
  if (!page) return 'home'
  if (pageSlug === 'albums' && secondarySlug) return PAGES.album.type
  if (pageSlug === 'china' && secondarySlug) return PAGES.album.type
  return page.type
}

export const getPageTypeAndSlug = (
  pathname: string,
): { pageSlug: PageSlug; pageType: PageType } => {
  const [, pageSlug, secondarySlug] = pathname.split('/')
  return {
    pageSlug: pageSlug as PageSlug,
    pageType: getTypeFromSlug(pageSlug as PageSlug, secondarySlug),
  }
}

// * TAILWIND
// ----------------

/** Merge classes with tailwind-merge with clsx full feature */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const getMediaQueryFromScreen = (
  breakpointDef: string,
  includeMedia = false,
) => {
  return `${includeMedia ? '@media ' : ''}(min-width: ${breakpointDef})`
}
// * SANITY
// ----------------
export const getSanityVideoAspectRatio = (
  video: SanityVideo,
  version?: 'number' | 'paddingTop',
): number | string => {
  const {
    data: { aspect_ratio },
  } = video.asset
  const [width, height] = aspect_ratio.split(':')
  const aR = parseFloat(width) / parseFloat(height)
  if (version === 'paddingTop') return `${(1 / aR) * 100}%`
  return aR
}

export const getSanityImageDims = (
  image: SanityImage,
): {
  width: number
  height: number
} => {
  const { width, height } = image.asset.metadata.dimensions
  return { width, height }
}

export const getSanityImageAspectRatio = (
  image: SanityImage,
  version?: 'number' | 'paddingTop',
): number | string => {
  const { width, height } = getSanityImageDims(image)
  const aR = width / height
  if (version === 'paddingTop') return `${(1 / aR) * 100}%`
  return aR
}

export function pauseAndMuteOtherMedia(el: HTMLMediaElement) {
  if (!el || !['VIDEO', 'AUDIO', 'MUX-PLAYER'].includes(el.nodeName)) return

  document
    .querySelectorAll<HTMLMediaElement>('audio, video, mux-player')
    .forEach((el) => {
      let mediaEl = el

      if (el.nodeName === 'MUX-PLAYER') {
        if (el.shadowRoot) {
          const muxVideo = el.shadowRoot.querySelector('mux-video')
          if (muxVideo && muxVideo.shadowRoot) {
            const videoEl = muxVideo.shadowRoot.querySelector('video')
            if (videoEl) {
              mediaEl = videoEl
            }
          }
        }
        // el.muted = true
        mediaEl.muted = true
      }

      // PAUSE audio elements
      if (el.nodeName === 'AUDIO') {
        mediaEl.pause()
      }

      // Just mute video elements (let them continue to play muted)
      if (el.nodeName === 'VIDEO') {
        mediaEl.muted = true
      }
    })

  el.play()
  el.muted = false
}

export const getYear = (dateObj: Date): string => {
  return dateObj.getFullYear().toString()
}

export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

export const getMonthName = (dateObj: Date): string => {
  return monthNames[dateObj.getMonth()]
}

export const formatDate = (date: string, includeMonth = false) => {
  // Output pattern should be "month name, yyyy"
  try {
    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) {
      return date // Return original string if invalid date
    }

    // Format as "Month name, yyyy"
    const year = getYear(dateObj)

    if (!includeMonth) return `${year}`

    const month = getMonthName(dateObj)
    return `${month} ${year}`
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error formatting date:', error)
    return date
  }
}

// * MATH
// ----------------

export const getRandomFloat = (min: number, max: number) => {
  return Math.random() * (max - min) + min
}

export const getRandomItemFromArray = <T>(arr: T[]) => {
  return arr[Math.floor(Math.random() * arr.length)]
}

export const generateRandomIndices = (count: number, max: number) => {
  const indices = new Uint32Array(count)
  for (let i = 0; i < count; i++) {
    indices[i] = Math.floor(Math.random() * max) * 3
  }
  return indices
}

export const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// * LISTENERS and ANIMATIONS
// --------------------------

export const debounce = <T>(
  func: (...args: T[]) => void,
  wait: number,
  immediate = false,
) => {
  let timeout: ReturnType<typeof setTimeout> | null
  return function (...args: T[]) {
    const later = () => {
      timeout = null
      if (!immediate) func(...args)
    }
    const callNow = immediate && !timeout
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func(...args)
  }
}

export const throttle = <T>(fn: (...args: T[]) => void, wait: number) => {
  let lastTime = 0
  return function (...args: T[]) {
    const now = new Date().getTime()
    if (now - lastTime >= wait) {
      lastTime = now
      fn(...args)
    }
  }
}

export const throttleRaf = <T>(fn: (...args: T[]) => void) => {
  let waiting = false
  return function (...args: T[]) {
    if (!waiting) {
      waiting = true
      requestAnimationFrame(() => {
        fn(...args)
        waiting = false
      })
    }
  }
}

// * SVG
// ----------------

const setSvgColor = (
  svgStrg: string,
  color: string,
  colorFrom: string,
): string => {
  const colorStrg = `fill="${color}"`
  const replaceRegex = new RegExp(`fill="${colorFrom}"`, 'g')
  const svgStrgColor = svgStrg.replace(replaceRegex, colorStrg)
  return svgStrgColor
}

const base64EncodeUnicode = (str: string) => {
  // Remove any characters outside the Latin1 range
  const decoded = unescape(encodeURIComponent(str))

  // Now we can use btoa to convert the svg to base64
  const base64 = btoa(decoded)
  return `data:image/svg+xml;base64,${base64}`
}

export const getSvgImg = async (
  svgSrc: string,
  color: string,
  colorFrom = 'black',
) => {
  const response = await fetch(svgSrc)
  const svg = await response.text()
  return new Promise((resolve) => {
    const coloredSvgString = setSvgColor(svg, color, colorFrom)
    const encodedSvg = base64EncodeUnicode(coloredSvgString)
    const img = new Image()
    img.src = encodedSvg
    img.onload = () => {
      resolve(img)
    }
  })
}
